import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Radio, RadioChangeEvent, Select, Slider } from "antd";
import dayjs, { Dayjs } from "dayjs";
import './filter.css'
import { getPopoverColumns } from "./FilterColumnConfigurations";
import { AppConstants } from "../../../Appconstants";
import { useLocation } from "react-router-dom";

interface IFilterMenuProps {
  filterData: any[];
  filterType: string;
  onApplyFilter: (filteredData: any[]) => void;
  applyItemFilters?: (filters: {
    categoryFilter: number | null;
    subCategoryFilter: number | null;
    microCategoryFilter: number | null;
    itemTypeFilter: number[];
    priceFilter: [number, number];
    dateFilter: [string, string] | null;
  }) => void;
  categoryData?: any[];
  documentType?: string;
  setIsDiscard?: Function;
  setDealStatus?: Function;
  setExitStatus?: Function;
  setDateRange?: Function;
  setDocumentTypeFilter?: Function;
  setLoading?: Function;
  setLabels?: Function;
  labelsData?: any;
  // Item filter props
  setCategoryFilter?: (value: number | null) => void;
  setSubCategoryFilter?: (value: number | null) => void;
  setMicroCategoryFilter?: (value: number | null) => void;
  setItemTypeFilter?: (value: number[]) => void;
  setPriceFilter?: (value: [number, number]) => void;
  setDateFilter?: (value: [string, string] | null) => void;
  resetAllFilters?: () => void;
  categoryFilter?: number | null;
  subCategoryFilter?: number | null;
  microCategoryFilter?: number | null;
  itemTypeFilter?: number[] | null;
  priceFilter?: [number, number] | null;
  dateFilter?: [string, string] | null;
}

const FilterMenu = forwardRef((props: IFilterMenuProps, ref) => {
  const {
    filterData,
    filterType,
    onApplyFilter,
    applyItemFilters,
    categoryData,
    documentType,
    setIsDiscard,
    setDealStatus,
    setExitStatus,
    setDateRange,
    setDocumentTypeFilter,
    setLoading,
    setLabels,
    labelsData,
    // Item filter props
    setCategoryFilter,
    setSubCategoryFilter,
    setMicroCategoryFilter,
    setItemTypeFilter,
    setPriceFilter,
    setDateFilter,
    resetAllFilters,
    categoryFilter,
    subCategoryFilter,
    microCategoryFilter,
    itemTypeFilter,
    priceFilter,
    dateFilter,
  } = props;

  const [isMinStockFilter, setIsMinStockFilter] = useState(false);
  const [isMaxStockFilter, setIsMaxStockFilter] = useState(false);
  const [priceRangeFilter, setPriceRangeFilter] = useState<[number, number]>(priceFilter || [0, 100000]);
  const [itemTypeFilterLocal, setItemTypeFilterLocal] = useState<number[]>(itemTypeFilter || []);
  const [createdAtRange, setCreatedAtRange] = useState<[Dayjs | null, Dayjs | null] | null>(
    dateFilter ? [dayjs(dateFilter[0]), dayjs(dateFilter[1])] : null
  );
  const [categoryFilterLocal, setCategoryFilterLocal] = useState<number | null>(categoryFilter || null);
  const [subCategoryFilterLocal, setSubCategoryFilterLocal] = useState<number | null>(subCategoryFilter || null);
  const [microCategoryFilterLocal, setMicroCategoryFilterLocal] = useState<number | null>(microCategoryFilter || null);
  const [docTypeFilter, setDocTypeFilter] = useState<string[]>([]);
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  const [transactionsDocumentTypeFilter, setTransactionsDocumentTypeFilter] = useState<string[]>([]);
  const [transactionsPaymentDueDateFilter, setTransactionsPaymentDueDateFilter] = useState("");
  const [documentStatusFilter, setDocumentStatusFilter] = useState<string[]>([]);
  const [dealStatusFilter, setDealStatusFilter] = useState<number[]>([]);
  const [exitStatusFilter, setExitStatusFilter] = useState<number[]>([]);
  const [productionStatusFilter, setProductionStatusFilter] = useState<string[]>([]);
  const [bomStatusFilter, setBOMStatusFilter] = useState<string[]>([]);

  const [bomCreatedByFilter, setBOMCreatedByFilter] = useState<string>("");
  const [ocCreatedFilter, setOCCreatedFilter] = useState<string[]>([]);
  const [fromStoreFilter, setFromStoreFilter] = useState<string[]>([]);
  const [toStoreFilter, setToStoreFilter] = useState<string[]>([]);
  const [transferredByFilter, setTransferredByFilter] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [microCategory, setMicroCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<any>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>('');
  const [fromStore, setFromStore] = useState('');
  const [toStore, setToStore] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedDateRangeRadio, setSelectedDateRangeRadio] = useState<string | null>(null);
  const location = useLocation();

  useImperativeHandle(ref, () => ({
    resetAllFilters: resetAllFiltersLocal,
  }));

  useEffect(() => {
    setFilters({});
    setDocTypeFilter([]);
    setLabelFilter([]);
    setDealStatusFilter([]);
    setBOMStatusFilter([]);
    setBOMCreatedByFilter("");
    setOCCreatedFilter([]);
    setFromStoreFilter([]);
    setToStoreFilter([]);
    setTransferredByFilter([]);
    setCreatedAtRange(null);
    setCategory('');
    setSubCategory('');
    setMicroCategory('');
    setSelectedCategory('');
    setSelectedSubCategory('');
    setFromStore('');
    setToStore('');
    setPriceRangeFilter([0, 100000]);
    setIsMinStockFilter(false);
    setIsMaxStockFilter(false);
    setItemTypeFilterLocal([]);
    setCategoryFilterLocal(null);
    setSubCategoryFilterLocal(null);
    setMicroCategoryFilterLocal(null);
    setSelectedDateRangeRadio(null);
  }, [documentType]);

  useEffect(() => {
    if (filterType !== "itemFilter") return;

    setCategoryFilterLocal(categoryFilter ?? null);
    setSubCategoryFilterLocal(subCategoryFilter ?? null);
    setMicroCategoryFilterLocal(microCategoryFilter ?? null);
  }, [
    filterType,
    categoryFilter,
    subCategoryFilter,
    microCategoryFilter,
  ]);

  const status = [
    { key: 'Draft', value: 0 },
    { key: 'Created', value: 1 },
    { key: 'Discard', value: 2 },
    ...(location.pathname.includes('purchase') ? [
      { key: 'Partially Received', value: 4 },
      { key: 'Fully Received', value: 5 },
      { key: 'Excess Received', value: 6 },
      { key: 'Quality Report Created', value: 7 },
      { key: 'Partially Ordered', value: 14 },
      { key: 'Partially Ordered, Partially Received', value: 15 },
      { key: 'Ordered', value: 16 },
      { key: 'Fully Ordered, Partially Received', value: 17 },
      { key: 'Fully Ordered, Fully Received', value: 18 },
      { key: 'Request for Approval', value: 29 },
      { key: 'Rejected', value: 30 }
    ] : []),

    ...(location.pathname.includes('sales') ? [
      { key: 'Sales Quotation Created', value: 8 },
      { key: 'OC Created', value: 9 },
      { key: 'Partially Delivered', value: 10 },
      { key: 'Completely Delivered', value: 11 },
      { key: 'Partially Billing', value: 12 },
      { key: 'Completely Billed', value: 13 },
      { key: 'Request for Approval', value: 29 },
      { key: 'Rejected', value: 30 }
    ] : [])
  ];



  const bomStatus: { key: string; value: string }[] = [
    { key: 'Draft', value: "0" },
    { key: 'Created', value: "1" },
  ];

  const filterStyle = {
    padding: "12px",
    marginBottom: "16px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  };

  const headingStyle = {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "8px",
  };

  const checkboxGroupStyle = {
    display: "block",
    marginTop: "8px",
  };

  const handleColumnFilter = (dataIndex: string, value: string) => {
    setFilters(prev => ({ ...prev, [dataIndex]: value }));
  };

  const renderReportFilter = () => {
    const columns = getPopoverColumns(documentType || "");

    return (
      <div className="filterBody">
        <div className="filterItems">
          {columns.map((column) => (
            <div key={column.dataIndex} style={filterStyle}>
              <strong style={headingStyle}>{column.title}</strong>
              <Input
                placeholder={`Filter by ${column.title}`}
                onChange={(e) => handleColumnFilter(column.dataIndex, e.target.value)}
                value={filters[column.dataIndex] || ''}
              />
            </div>
          ))}
        </div>
        <div className="filterFooter">
          {renderApplyFilter()}
        </div>
      </div>
    );
  };

  const applyFilters = () => {
    let filteredData = [...filterData];
    if (filterType === "reportFilter" && documentType) {
      if (documentType === AppConstants.DOCUMENT_TYPE.PURCHASE_ORDER) {
        filteredData = filteredData.filter((item) => {
          return (
            item?.documentNumber?.toLowerCase().includes(filters["documentNumber"]?.toLowerCase())
          );
        });

        // Apply other PO specific filters
      }
    }

    if (filterType === "itemFilter") {
      const formattedDateFilter =
        createdAtRange && createdAtRange[0] && createdAtRange[1]
          ? [
            createdAtRange[0].format('YYYY-MM-DD'),
            createdAtRange[1].format('YYYY-MM-DD')
          ] as [string, string]
          : null;

      if (applyItemFilters) {
        applyItemFilters({
          categoryFilter: categoryFilterLocal,
          subCategoryFilter: subCategoryFilterLocal,
          microCategoryFilter: microCategoryFilterLocal,
          itemTypeFilter: itemTypeFilterLocal || [],
          priceFilter: priceRangeFilter,
          dateFilter: formattedDateFilter,
        });
      } else {
        // Fallback for legacy callers
        setCategoryFilter?.(categoryFilterLocal || null);
        setSubCategoryFilter?.(subCategoryFilterLocal || null);
        setMicroCategoryFilter?.(microCategoryFilterLocal || null);
        setItemTypeFilter?.(itemTypeFilterLocal || []);
        setPriceFilter?.(priceRangeFilter);
        setDateFilter?.(formattedDateFilter);
      }

      // Close the filter popover after applying
      onApplyFilter([]);
      return;
    }

    if (filterType === "inventoryServiceFilter") {
      filteredData = category ? filteredData?.filter((item) => item.category == category) : filteredData;
      filteredData = subCategory ? filteredData?.filter((item) => item.subCategory == subCategory) : filteredData;
      filteredData = microCategory ? filteredData?.filter((item) => item.microCategory == microCategory) : filteredData;
    }

    if (filterType === "documentFilter") {
      if (docTypeFilter.length >= 0) {
        setLoading?.(true);
        setDocumentTypeFilter?.(docTypeFilter);
        filteredData = filteredData?.filter((doc) => docTypeFilter.includes(doc.documentType));
      }

      if (labelFilter.length >= 0) {
        setLoading?.(true);
        setLabels?.(labelFilter);
        // filteredData = filteredData?.filter((doc) => docTypeFilter.includes(doc.documentType));
      }

      if (dealStatusFilter?.length >= 0) {
        setLoading?.(true);
        setDealStatus?.(dealStatusFilter);
        filteredData = filteredData.filter((doc: any) => {
          return dealStatusFilter.includes(doc?.status);
        });
      }

      if (ocCreatedFilter.length > 0) {
        filteredData = filteredData.filter((doc) =>
          (ocCreatedFilter.includes("yes") && doc.OCDate !== null) ||
          (ocCreatedFilter.includes("no") && doc.OCDate === null)
        );
      }
    }

    if (filterType === "gateEntryFilter") {
      if (exitStatusFilter?.length >= 0) {
        setLoading?.(true);
        setExitStatus?.(exitStatusFilter);
      }
    }

    if (filterType === "ServiceOrderFilter") {
      if (docTypeFilter.length >= 0) {
        setDocumentTypeFilter?.(docTypeFilter);
        filteredData = filteredData?.filter((doc) => docTypeFilter.includes(doc.documentType));
      }

      if (dealStatusFilter?.length >= 0) {
        setLoading?.(true);
        setDealStatus?.(dealStatusFilter);
        filteredData = filteredData.filter((doc: any) => {
          return dealStatusFilter.includes(doc?.status);
        });
      }

      if (labelFilter.length >= 0) {
        setLabels?.(labelFilter);
        filteredData = filteredData?.filter((doc) => docTypeFilter.includes(doc.documentType));
      }
    }

    if (filterType === "bomFilter") {
      onApplyFilter(
        { bomStatus: bomStatusFilter, dateRange: createdAtRange, createdBy: bomCreatedByFilter, } as unknown as any[]
      );
      return;
    }

    if (filterType === "transactionsFilter") {
      const filterObject = {
        status: documentStatusFilter,
        documentType: transactionsDocumentTypeFilter,
        paymentDueDate: transactionsPaymentDueDateFilter,
      };
      onApplyFilter(filterObject as unknown as any[]);
      return;
    }

    if (filterType === "historyFilter") {
      if (fromStore) {
        filteredData = filteredData?.filter((item) =>
          item?.fromStore?.name == fromStore
        );
      }
      if (toStore) {
        filteredData = filteredData?.filter((item) =>
          item?.toStore?.name == toStore
        );
      }

      if (transferredByFilter?.length > 0) {
        filteredData = filteredData?.filter((item) =>
          transferredByFilter?.includes(item?.transferredBy)
        );
      }
    }

    if (createdAtRange && createdAtRange[0] && createdAtRange[1]) {
      const [startDate, endDate] = createdAtRange;
      setDateRange?.([startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')]);
      filteredData = filteredData.filter((item) => {
        const itemDate = dayjs(item?.createdAt);
        return (
          (itemDate.isAfter(startDate, "day") &&
            itemDate.isBefore(endDate, "day")) ||
          itemDate.isSame(startDate, "day") ||
          itemDate.isSame(endDate, "day")
        );
      });
    }
    setFilteredData(filteredData);
    onApplyFilter(filteredData);
  };



  const handleItemTypeChange = (checkedValues: string[]) => {
    // Convert string keys to numeric IDs: buy=1, sell=2, both=3
    const numericValues = checkedValues
      .map((val: string): number | null => {
        if (val === "buy") return 1;
        if (val === "sell") return 2;
        if (val === "both") return 3;
        return null;
      })
      .filter((val): val is number => val !== null);

    setItemTypeFilterLocal(numericValues);
  };

  const handlePriceRangeChange = (value: [number, number]) => {
    setPriceRangeFilter(value);
  };

  const handleDocTypeFilterChange = (selectedValues: string[]) => {
    setDocTypeFilter(selectedValues);
  };
  const handleLabelChange = (selectedValues: string[]) => {
    setLabelFilter(selectedValues);
  };

  const handleTransactionsDocumentTypeFilter = (selectedValues: string[]) => {
    setTransactionsDocumentTypeFilter(selectedValues);
  };

  const handleTransactionsPaymentDueDateFilter = (
    date: Dayjs | null,
    dateString: string | string[]
  ) => {
    const finalDateString = Array.isArray(dateString)
      ? dateString[0] || ""
      : dateString || "";

    setTransactionsPaymentDueDateFilter(finalDateString);
  };

  const handleDealStatusFilterChange = (selectedValues: number[]) => {
    setDealStatusFilter(selectedValues);
    setIsDiscard?.(selectedValues.includes(2));
  };

  const handleExitStatusFilterChange = (selectedValues: number[]) => {
    setExitStatusFilter(selectedValues);
  };

  const handleBOMStatusFilterChange = (selectedValues: string[]) => {
    setBOMStatusFilter(selectedValues);
  };

  const handleBOMCreatedByFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBOMCreatedByFilter(e.target.value);
  };

  const handleFromStoreChange = (selectedValues: string[]) => {
    setFromStoreFilter(selectedValues);
  };

  const handleToStoreChange = (selectedValues: string[]) => {
    setToStoreFilter(selectedValues);
  };

  const handleTransferredByChange = (selectedValues: string[]) => {
    setTransferredByFilter(selectedValues);
  };


  const renderFilterByType = () => (
    <div style={filterStyle}>
      <strong style={headingStyle}>Filter by Type</strong>
      <Checkbox.Group
        style={{ display: "block", marginTop: 8 }}
        onChange={handleItemTypeChange}
        value={
          itemTypeFilterLocal
            ? itemTypeFilterLocal.map(id => {
              if (id === 1) return 'buy';
              if (id === 2) return 'sell';
              if (id === 3) return 'both';
              return '';
            }).filter(Boolean)
            : []
        }
      >
        <Checkbox value="buy">Buy</Checkbox>
        <Checkbox value="sell">Sell</Checkbox>
        <Checkbox value="both">Both</Checkbox>
      </Checkbox.Group>
    </div>
  );

  const renderDocumentTypeFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Doc Type</strong>
      <br />
      <Checkbox.Group
        value={docTypeFilter}
        onChange={handleDocTypeFilterChange}>
        {location.pathname.includes('sales') ?
          <>
            <Checkbox value="Sales Lead">Sales Lead</Checkbox>
            <Checkbox value="Sales Quotation">Sales Quotation</Checkbox>
            <Checkbox value="Sales Order">Sales Order</Checkbox>
            <Checkbox value="Delivery Challan">Delivery Challan</Checkbox>
            <Checkbox value="Invoice">Invoice</Checkbox>
            <Checkbox value="Sales Return">Sales Return</Checkbox>
          </> :
          location.pathname.includes('purchase') ? <>
            <Checkbox value="Purchase Request">Purchase Request</Checkbox>
            <Checkbox value="Purchase Order">Purchase Order</Checkbox>
            <Checkbox value="Goods Received Note">Goods Received Note</Checkbox>
            <Checkbox value="Quality Report">Quality Report</Checkbox>
            <Checkbox value="Purchase Invoice">Purchase Invoice</Checkbox>
            <Checkbox value="Purchase Debit Note">Purchase Debit Note</Checkbox>
            <Checkbox value="Purchase Credit Note">Purchase Credit Note</Checkbox>
            <Checkbox value="Purchase Return">Purchase Return</Checkbox>
          </> :
            location.pathname.includes('documentServicesConfirmation') ? <>
              <Checkbox value="Service Confirmation">Service Confirmation</Checkbox>
              <Checkbox value="Service Confirmation Grn">Service Confirmation Grn</Checkbox>
              <Checkbox value="Service Confirmation Qr">Service Confirmation Qr</Checkbox>
              <Checkbox value="Service Confirmation Challan">Service Confirmation Challan</Checkbox>
              <Checkbox value="Service Confirmation Proforma Invoice">Proforma Invoice</Checkbox>
              <Checkbox value="Service Confirmation Invoice">Service Confirmation Invoice</Checkbox>
              <Checkbox value="Service Confirmation Credit Note">Service Confirmation Credit Note</Checkbox>
              <Checkbox value="Service Confirmation Debit Note">Service Confirmation Debit Note</Checkbox>
            </> :
              <>
                <Checkbox value="Service Order">Service Order</Checkbox>
                <Checkbox value="Service Challan">Service Challan</Checkbox>
                <Checkbox value="Service Grn">Service Grn</Checkbox>
                <Checkbox value="Service Qr">Quality Report</Checkbox>
                <Checkbox value="Service Invoice">Service Invoice</Checkbox>
                <Checkbox value="Service Credit Note">Service Credit Note</Checkbox>
                <Checkbox value="Service Debit Note">Service Debit Note</Checkbox>
              </>
        }
      </Checkbox.Group>
    </div>
  );

  const renderTransactionsDocumentTypeFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Transaction Doc Type</strong>
      <br />
      <Checkbox.Group
        value={transactionsDocumentTypeFilter}
        onChange={handleTransactionsDocumentTypeFilter}
      >
        {location.pathname.includes("sales") ? (
          <>
            <Checkbox value="Invoice">Invoice</Checkbox>
            <Checkbox value="Debit Note">Debit Note</Checkbox>
            <Checkbox value="Purchase Debit Note">Purchase Debit Note</Checkbox>
          </>
        ) : (
          <>
            <Checkbox value="Credit Note">Credit Note</Checkbox>
            <Checkbox value="Purchase Credit Note">
              Purchase Credit Note
            </Checkbox>
            <Checkbox value="Purchase Invoice">Purchase Invoice</Checkbox>
          </>
        )}
      </Checkbox.Group>
    </div>
  );

  const renderTransactionsPaymentDueDateFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Transaction Due Date</strong>
      <br />
      <DatePicker
        style={{ marginTop: 8, width: "100%" }}
        placeholder="Select due date"
        value={
          transactionsPaymentDueDateFilter
            ? dayjs(transactionsPaymentDueDateFilter)
            : null
        }
        onChange={handleTransactionsPaymentDueDateFilter}
        format="YYYY-MM-DD"
      />
    </div>
  );

  const renderByDealStatusFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Document Status</strong>
      <br />
      <Checkbox.Group
        onChange={handleDealStatusFilterChange}
        value={dealStatusFilter}
      >
        {
          status?.map((data, ind: number) => <Checkbox key={ind} value={data?.value}>{data?.key}</Checkbox>)
        }
      </Checkbox.Group>
    </div>
  );

  const renderByExitStatusFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Exit Status</strong>
      <br />
      <Checkbox.Group
        onChange={handleExitStatusFilterChange}
        value={exitStatusFilter}
      >
        <Checkbox value={1}>Exited</Checkbox>
        <Checkbox value={0}>Pending</Checkbox>
      </Checkbox.Group>
    </div>
  )

  const renderByLabel = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Label</strong>
      <br />
      <Checkbox.Group
        onChange={handleLabelChange}
        value={labelFilter}
      >
        {
          labelsData?.map((data: any, ind: number) => <Checkbox key={ind} value={data?.label}>{data?.label}</Checkbox>)
        }
      </Checkbox.Group>
    </div>
  );

  const renderByBOMStatusFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Filter by Status</strong>
      <br />
      <Checkbox.Group onChange={handleBOMStatusFilterChange} value={bomStatusFilter}>
        {
          bomStatus?.map((data, ind: number) => <Checkbox key={ind} value={data?.value}>{data?.key}</Checkbox>)
        }
      </Checkbox.Group>
    </div>
  );

  // const renderDocumentStatusFilter = () => (
  //   <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
  //     <strong style={headingStyle}>Filter by Status</strong>
  //     <br />
  //     <Checkbox.Group
  //       value={documentStatusFilter}
  //       onChange={(checkedValues) =>
  //         setDocumentStatusFilter(checkedValues as string[])
  //       }
  //     >
  //       <Checkbox value="payable">Payable</Checkbox>
  //       <Checkbox value="paid">Paid</Checkbox>
  //       <Checkbox value="receivable">Receivable</Checkbox>
  //       <Checkbox value="received">Received</Checkbox>
  //     </Checkbox.Group>
  //   </div>
  // );

  const renderByBOMCreatedByFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Created By</strong>
      <br />
      <Input
        placeholder="Enter creator name"
        value={bomCreatedByFilter}
        onChange={handleBOMCreatedByFilterChange}
        style={{ marginTop: 8 }}
      />
    </div>
  );

  const renderFromStoreFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>From Store</strong>
      {filterType === 'historyFilter' ?
        <Select
          style={{ display: 'flex' }}
          showSearch
          placeholder="Please choose from store"
          value={fromStore}
          onChange={(value: any) => {
            setFromStore(value);
          }}
        >
          <Select.Option key='Select Category' value=''>
            All From store
          </Select.Option>
          {Array.from(
            new Map(
              filterData
                ?.filter((data: any) => data?.fromStore?.name !== 'Unknown Store')
                .map((data: any) => [data.fromStore.name, data])
            ).values()
          )?.map((data: any, index: number) => (
            <Select.Option onSelect={() => {
            }} key={data?.fromStore?.name} value={data?.fromStore?.name}>
              {data?.fromStore?.name}
            </Select.Option>
          ))}
        </Select>
        :
        <Checkbox.Group
          style={checkboxGroupStyle}
          onChange={handleFromStoreChange}
          options={Array.from(new Set(filterData?.map((item) => item?.fromStore)))}
        />}
    </div>
  );

  const renderToStoreFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>To Store</strong>
      {filterType === 'historyFilter' ?
        <Select
          style={{ display: 'flex' }}
          showSearch
          placeholder="Please choose from store"
          value={toStore}
          onChange={(value: any) => {
            setToStore(value);
          }}
        >
          <Select.Option key='Select Category' value=''>
            All To store
          </Select.Option>
          {Array.from(
            new Map(
              filterData
                ?.filter((data: any) => data?.toStore?.name !== 'Unknown Store')
                .map((data: any) => [data.toStore.name, data])
            ).values()
          )?.map((data: any, index: number) => (
            <Select.Option onSelect={() => {
            }} key={data?.toStore?.name} value={data?.toStore?.name}>
              {data?.toStore?.name}
            </Select.Option>
          ))}
        </Select>
        :
        <Checkbox.Group
          style={checkboxGroupStyle}
          onChange={handleToStoreChange}
          options={Array.from(new Set(filterData?.map((item) => item?.toStore)))}
        />}
    </div>
  );

  const renderTransferredByFilter = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Transferred By</strong>
      <Checkbox.Group
        style={checkboxGroupStyle}
        onChange={handleTransferredByChange}
        options={Array.from(new Set(filterData?.map((item) => item?.transferredBy)))}
      />
    </div>
  );

  const renderPriceRange = () => (
    <div style={filterStyle}>
      <strong style={headingStyle}>Price Range</strong>
      <Slider
        range
        value={priceRangeFilter}
        onChange={(value) => handlePriceRangeChange(value as [number, number])}
        max={100000}
        step={500}
        tooltip={{ formatter: (value) => `₹ ${value}` }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666' }}>
        <span>₹{priceRangeFilter[0]}</span>
        <span>₹{priceRangeFilter[1]}</span>
      </div>
    </div>
  );

  const renderDateRange = () => (
    <div style={filterStyle}>
      <strong style={headingStyle}>Date Range</strong>
      <Radio.Group
        onChange={handleDateSelect}
        value={selectedDateRangeRadio}
      >
        <Radio value="last7Days">Last 7 Days</Radio>
        <Radio value="last30Days">Last 30 Days</Radio>
        <Radio value="last3Months">Last 3 Months</Radio>
        <Radio value="last6Months">Last 6 Months</Radio>
      </Radio.Group>
    </div>
  );

  const handleDateSelect = (e: RadioChangeEvent) => {
    const value = e.target.value;
    setSelectedDateRangeRadio(value);
    const today = dayjs();
    let startDate;

    switch (value) {
      case "last7Days":
        startDate = today.subtract(7, "days");
        break;
      case "last30Days":
        startDate = today.subtract(30, "days");
        break;
      case "last3Months":
        startDate = today.subtract(3, "months");
        break;
      case "last6Months":
        startDate = today.subtract(6, "months");
        break;
      default:
        startDate = null;
    }
    setCreatedAtRange([startDate, today]);
  };

  const renderCustomDateRange = () => (
    <div onClick={(e) => e.stopPropagation()} style={filterStyle}>
      <strong style={headingStyle}>Custom Date</strong>
      <DatePicker.RangePicker
        style={{ width: '100%' }}
        value={createdAtRange}
        onChange={(dates) => {
          setCreatedAtRange(dates);
          setSelectedDateRangeRadio(null);
        }}
        disabledDate={(current) => {
          return current && current.isAfter(dayjs(), "day");
        }}
      />
    </div>
  );

  const renderApplyFilter = () => (
    <div style={{ display: 'flex', gap: 8 }}>
      <Button type="primary" onClick={applyFilters} style={{ flex: 1 }}>
        Apply Filters
      </Button>
      <Button onClick={resetAllFiltersLocal} style={{ flex: 1 }}>
        Reset
      </Button>
    </div>
  );

  const resetAllFiltersLocal = () => {
    // Handle item filter reset differently - call parent's resetAllFilters
    if (filterType === "itemFilter") {
      if (resetAllFilters) {
        resetAllFilters();
      }
      // Also reset local states for item filter
      setCategoryFilterLocal(null);
      setSubCategoryFilterLocal(null);
      setMicroCategoryFilterLocal(null);
      setItemTypeFilterLocal([]);
      setPriceRangeFilter([0, 100000]);
      setCreatedAtRange(null);
      return;
    }

    setIsMinStockFilter(false);
    setIsMaxStockFilter(false);
    setPriceRangeFilter([0, 100000]);
    setItemTypeFilterLocal([]);
    setCreatedAtRange(null);
    setCategory("");
    setSubCategory("");
    setMicroCategory("");
    setSelectedCategory("");
    setSelectedSubCategory("");
    setTransactionsDocumentTypeFilter([]);
    setTransactionsPaymentDueDateFilter("");
    setBOMStatusFilter([]);
    setBOMCreatedByFilter("");
    setSelectedDateRangeRadio(null);
    setDocTypeFilter([]);
    setDealStatusFilter([]);
    setDocumentStatusFilter([]);
    setFromStoreFilter([]);
    setToStoreFilter([]);
    setTransferredByFilter([]);
    setFromStore("");
    setToStore("");
    setFilters({});
    setLabels?.([]);
    setLabelFilter([]);

    if (setIsDiscard) setIsDiscard(false);
    if (setDealStatus) setDealStatus([]);
    if (setExitStatus) setExitStatus([]);
    if (setDateRange) setDateRange("");
    if (setDocumentTypeFilter) setDocumentTypeFilter([]);

    if (filterType === "documentFilter") {
      if (setLoading) setLoading(true);
    } else {
      if (setLoading) setLoading(false);
    }

    if (filterType === "gateEntryFilter") {
      if (setLoading) setLoading(true);
    } else {
      if (setLoading) setLoading(false);
    }

    const isSalesContext = window.location.href.includes("sales") || documentType === "sales";
    const isPurchaseContext = window.location.href.includes("purchase") || documentType === "purchase";

    // Filter data based on context
    let contextFilteredData = [...filterData];

    if (isSalesContext) {
      contextFilteredData = filterData.filter(
        (doc: any) => doc.documentType === "sales" || doc.type === "sales"
      );
    } else if (isPurchaseContext) {
      contextFilteredData = filterData.filter(
        (doc: any) => doc.documentType === "purchase" || doc.type === "purchase"
      );
    }

    onApplyFilter(contextFilteredData);
  };

  const renderGenericFilter = () => {
    if (filterType === "reportFilter") {
      return renderReportFilter();
    }

    if (filterType === "itemFilter") {
      const selectedCategoryData = categoryData?.find(
        (category: any) => category.id === categoryFilterLocal
      );
      const selectedSubCategoryData = selectedCategoryData?.child?.find(
        (subCategory: any) => subCategory.id === subCategoryFilterLocal
      );

      return (
        <>
          <div className="filterBody">
            <div className="filterItems">
              <div className="categoryContainer" style={{ marginBottom: 16 }}>
                <div>
                  <strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>
                    Filter By Category
                  </strong>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Please choose the category"
                    optionFilterProp="children"
                    value={categoryFilterLocal ?? ''}
                    onChange={(value: any) => {
                      const nextCategoryId = value ? Number(value) : null;
                      setCategoryFilterLocal(nextCategoryId);
                      setSubCategoryFilterLocal(null);
                      setMicroCategoryFilterLocal(null);
                    }}
                  >
                    <Select.Option key='Select Category' value=''>
                      All Category
                    </Select.Option>
                    {categoryData?.map((category: any, index: number) => (
                      <Select.Option key={category.id} value={category.id}>
                        {category.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Please choose the sub category"
                    value={subCategoryFilterLocal ?? ''}
                    onChange={(value: any) => {
                      const nextSubCategoryId = value ? Number(value) : null;
                      setSubCategoryFilterLocal(nextSubCategoryId);
                      setMicroCategoryFilterLocal(null);
                    }}
                    disabled={!categoryFilterLocal}
                  >
                    <Select.Option key='Select Sub Category' value=''>
                      All Sub Category
                    </Select.Option>
                    {selectedCategoryData?.child?.map((subCategory: any) => (
                      <Select.Option
                        key={subCategory.id}
                        value={subCategory.id}
                      >
                        {subCategory.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Please choose the micro category"
                    value={microCategoryFilterLocal ?? ''}
                    onChange={(value: any) => {
                      const nextMicroCategoryId = value ? Number(value) : null;
                      setMicroCategoryFilterLocal(nextMicroCategoryId);
                    }}
                    disabled={!subCategoryFilterLocal}
                  >
                    <Select.Option value='' key='Select Micro Category'>
                      All Micro Category
                    </Select.Option>
                    {selectedSubCategoryData?.child?.map(
                      (microCategory: any) => (
                        <Select.Option
                          key={microCategory.id}
                          value={microCategory.id}
                        >
                          {microCategory.name}
                        </Select.Option>
                      )
                    )}
                  </Select>
                </div>
              </div>
              {renderFilterByType()}
              {renderPriceRange()}
              {renderDateRange()}
              {renderCustomDateRange()}
            </div>
            <div className="filterFooter">
              {renderApplyFilter()}
            </div>
          </div>
        </>
      );
    }

    if (filterType === "inventoryServiceFilter") {
      return (
        <>
          <div className="categoryContainer">
            <div>
              <strong>Filter By Category</strong>
            </div>
            <div>
              <Select
                style={{ display: "flex" }}
                showSearch
                placeholder="Please choose the category"
                optionFilterProp="children"
                value={category}
                onChange={(value: any) => {
                  const index = categoryData?.findIndex(
                    (category) => category.id === value
                  );
                  setSubCategory("");
                  setMicroCategory("");
                  setSelectedCategory(index);
                  setSelectedSubCategory("");
                  setCategory(value);
                }}
              >
                <Select.Option key="Select Category" value="">
                  All Category
                </Select.Option>
                {categoryData?.map((category: any, index: number) => (
                  <Select.Option
                    onSelect={() => { }}
                    key={category.id}
                    value={category.id}
                  >
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <Select
                style={{ display: "flex" }}
                placeholder="Please choose the sub category"
                value={subCategory}
                onChange={(value: any) => {
                  const index =
                    categoryData &&
                    categoryData[selectedCategory]?.child?.findIndex(
                      (subCategory: any) => subCategory.id === value
                    );
                  setSubCategory(value);
                  setSelectedSubCategory(index);
                  setMicroCategory("");
                }}
                disabled={selectedCategory === ""}
              >
                <Select.Option key="Select Sub Category" value="">
                  All Sub Category
                </Select.Option>
                {selectedCategory != -1 &&
                  categoryData &&
                  categoryData[selectedCategory]?.child.map(
                    (subCategory: any) => (
                      <Select.Option
                        key={subCategory.id}
                        value={subCategory.id}
                      >
                        {subCategory.name}
                      </Select.Option>
                    )
                  )}
              </Select>
            </div>
            <div>
              <Select
                style={{ display: "flex" }}
                placeholder="Please choose the micro category"
                value={microCategory}
                onChange={(value: any) => setMicroCategory(value)}
                disabled={selectedSubCategory === ""}
              >
                <Select.Option value="" disabled key="Select Micro Category">
                  Select Micro Category
                </Select.Option>
                {categoryData &&
                  categoryData[selectedCategory]?.child[
                    selectedSubCategory
                  ]?.child?.map((microCategory: any) => (
                    <Select.Option
                      key={microCategory.id}
                      value={microCategory.id}
                    >
                      {microCategory.name}
                    </Select.Option>
                  ))}
              </Select>
            </div>
          </div>
          <div className="filterFooter">{renderApplyFilter()}</div>
        </>
      );
    }

    if (filterType === "bomFilter") {
      return (
        <>
          <div className="filterBody">
            <div className="">
              {renderByBOMStatusFilter()}
              {renderByBOMCreatedByFilter()}
              {renderDateRange()}
              {renderCustomDateRange()}
            </div>
            <div className="filterFooter" style={{ display: "flex", gap: 8 }}>
              {renderApplyFilter()}
            </div>
          </div>
        </>
      );
    }

    if (filterType === "transactionsFilter") {
      return (
        <>
          <div className="filterBody">
            <div className="">
              {/* {renderDocumentStatusFilter()} */}
              {renderTransactionsDocumentTypeFilter()}
              {renderTransactionsPaymentDueDateFilter()}
            </div>
            <div className="filterFooter" style={{ display: "flex", gap: 8 }}>
              {renderApplyFilter()}
            </div>
          </div>
        </>
      );
    }

    if (filterType === "documentFilter") {
      return (
        <>
          <div className="filterBody">
            <div className="filterItems">
              {renderDocumentTypeFilter()}
              {renderByDealStatusFilter()}
              {renderByLabel()}
              {/* {renderOCCreatedFilter()} */}
              {renderDateRange()}
              {renderCustomDateRange()}
            </div>
            <div className="filterFooter">
              {renderApplyFilter()}
            </div>
          </div >
        </>
      );
    }

    if (filterType === "gateEntryFilter") {
      return (
        <>
          <div className="filterBody">
            <div className="filterItems">
              {renderByExitStatusFilter()}
              {renderDateRange()}
              {renderCustomDateRange()}
            </div>
            <div className="filterFooter">
              {renderApplyFilter()}
            </div>
          </div >
        </>
      )
    }

    if (filterType === "ServiceOrderFilter") {
      return (
        <>
          <div className="filterBody">
            <div className="filterItems">
              {renderDocumentTypeFilter()}
              {renderByDealStatusFilter()}
              {renderByLabel()}
              {/* {renderOCCreatedFilter()} */}
              {renderDateRange()}
              {renderCustomDateRange()}
            </div>
            <div className="filterFooter">
              {renderApplyFilter()}
            </div>
          </div >
        </>
      );
    }




    if (filterType === "historyFilter") {
      return (
        <>
          <div className="filterBody">
            <div className="filterItems">
              {renderFromStoreFilter()}
              {renderToStoreFilter()}
              {renderTransferredByFilter()}
              {/* {renderDateRange()}
              {renderCustomDateRange()} */}
            </div>
            <div className="filterFooter">
              {renderApplyFilter()}
            </div>
          </div >
        </>
      );
    }
    return null;
  };

  return <div>{renderGenericFilter()}</div>;
});

export default FilterMenu;
